import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Search, Power, Loader2 } from "lucide-react";
import { mh } from "@/lib/mh";
import { http, errText } from "@/lib/http";
import { money } from "@/lib/onb";
import { Eyebrow, Panel } from "@/components/mh/bits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMhStream } from "@/lib/mh";
import { toast } from "sonner";

const READINESS = {
  live: ["Live", "#D4FF3A"],
  verified: ["Verified", "#58E8A3"],
  configured: ["Configured", "#8AB4FF"],
  selected: ["Selected", "#FFB020"],
  blocked: ["Blocked", "#FF3B5C"],
  disabled: ["Off", "#5A5A66"],
};

const ReadinessPill = ({ state, missing }) => {
  const [label, color] = READINESS[state] || READINESS.selected;
  return (
    <span className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5" style={{ color }} data-testid={`ai-readiness-${state}`} title={missing && missing.length ? `Missing: ${missing.join(", ")}` : label}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
      {label}
    </span>
  );
};

const Filter = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 rounded-full bg-white/[0.03] border-white/10 text-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-[#0A0A0F] border-white/10">
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const RoleRow = ({ role, hex, readiness, isActive, onToggle, toggling }) => (
  <div className="flex items-center gap-3 py-3 pl-12 pr-4 border-l-2 ml-6 hover:bg-white/[0.03] transition-colors group"
    style={{ borderColor: `${hex}44` }} data-testid={`ai-role-${role.slug}`}>
    <Link to={`/ai-team/${role.slug}`} className="flex-1 min-w-0" data-testid={`ai-role-link-${role.slug}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm font-medium truncate">{role.name}</span>
        {readiness && <ReadinessPill state={readiness.state} missing={readiness.missing_capabilities} />}
      </div>
      <div className="flex flex-wrap gap-x-3 mt-1 font-mono text-[10px] text-muted-foreground">
        <span>{role.stage}</span>
        {role.autonomy && <span>{role.autonomy}</span>}
        {role.wave && <span>Wave {role.wave}</span>}
        {role.one_liner && <span className="hidden sm:inline opacity-70 truncate max-w-sm">{role.one_liner}</span>}
      </div>
    </Link>
    {role.value && role.value.money_high > 0 && (
      <span className="font-mono text-xs text-[#D4FF3A] hidden sm:block whitespace-nowrap">${money(role.value.money_low)}–{money(role.value.money_high)}/mo</span>
    )}
    <button onClick={(e) => { e.stopPropagation(); onToggle(role, !isActive); }} disabled={toggling}
      data-testid={`ai-toggle-${role.slug}`} title={isActive ? "Turn this role off" : "Turn this role on"}
      className={`shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border font-mono text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40 ${isActive ? "border-[#D4FF3A]/40 text-[#D4FF3A] hover:bg-[#D4FF3A]/10" : "border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30"}`}>
      {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Power className="w-3 h-3" />}
      {isActive ? "On" : "Turn on"}
    </button>
  </div>
);

const DeptFolder = ({ dept, roles, readinessMap, activeSet, open, onToggle, mode, onRoleToggle, togglingSet }) => {
  const liveCount = roles.filter((r) => (readinessMap[r.slug] || {}).state === "live").length;
  const blockedCount = roles.filter((r) => (readinessMap[r.slug] || {}).state === "blocked").length;
  return (
    <div data-testid={`ai-dept-${dept.slug}`} className="border border-white/[0.07] rounded-2xl bg-white/[0.015] overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.03] transition-colors" data-testid={`ai-dept-toggle-${dept.slug}`}>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <span className="w-10 h-10 rounded-full border flex items-center justify-center font-mono text-[11px]" style={{ borderColor: dept.hex, color: dept.hex, boxShadow: `0 0 18px ${dept.hex}33` }}>
          {roles.length}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-display text-2xl" style={{ color: dept.hex }}>{dept.name}</span>
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
              {liveCount} live · {roles.length - liveCount} not yet
              {blockedCount > 0 && <span className="text-[#FF3B5C]"> · {blockedCount} blocked</span>}
            </span>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">{dept.tagline || dept.one_liner}</div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{mode}</span>
      </button>
      {open && (
        <div className="pb-2">
          {roles.length === 0 ? (
            <div className="pl-16 pr-4 py-4 text-sm text-muted-foreground">No {mode === "ACTIVE" ? "active" : ""} roles in this department{mode === "ACTIVE" ? " yet — expand to Full Atlas to see what's available." : ""}.</div>
          ) : (
            roles.map((r) => <RoleRow key={r.slug} role={r} hex={dept.hex} readiness={readinessMap[r.slug]} isActive={activeSet.has(r.slug)} onToggle={onRoleToggle} toggling={togglingSet.has(r.slug)} />)
          )}
        </div>
      )}
    </div>
  );
};

export default function AiTeam() {
  const [mode, setMode] = useState("active"); // "active" | "atlas"
  const [depts, setDepts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tailored, setTailored] = useState([]);
  const [readiness, setReadiness] = useState({});
  const [filters, setFilters] = useState({ department: "all", stage: "all", status: "all" });
  const [search, setSearch] = useState("");
  const [openDept, setOpenDept] = useState({});
  const [toggling, setToggling] = useState(new Set());

  const loadReadiness = () => http.get("/mh/readiness").then((r) => {
    const map = {};
    for (const it of r.data.items || []) { map[it.slug] = it; }
    setReadiness(map);
    setTailored((r.data.items || []).map((i) => i.slug));
  }).catch(() => {});

  useEffect(() => {
    mh.departments().then((r) => setDepts(r.data)).catch(() => {});
    mh.agents({}).then((r) => setAgents(r.data)).catch(() => {});
    loadReadiness();
  }, []);

  // Live readiness: react to tool verify / tailor toggle / blocked receipt events.
  useMhStream((ev) => {
    if (ev.type === "readiness.changed") {
      if (ev.item && ev.slug) {
        setReadiness((m) => ({ ...m, [ev.slug]: ev.item }));
        setTailored((prev) => ev.item.state === "disabled" ? prev.filter((s) => s !== ev.slug) : (prev.includes(ev.slug) ? prev : [...prev, ev.slug]));
      } else if (ev.items) {
        const map = {};
        for (const it of ev.items) { map[it.slug] = it; }
        setReadiness(map);
        setTailored(ev.items.map((i) => i.slug));
      }
    }
    if (ev.type === "tailor.role" && ev.slug) {
      // Fallback: refetch just the one role so legacy clients still get fresh pills.
      http.get(`/mh/readiness/${ev.slug}`).then((r) => setReadiness((m) => ({ ...m, [ev.slug]: r.data }))).catch(() => {});
    }
  });

  const toggleRole = async (role, nextEnabled) => {
    setToggling((s) => new Set(s).add(role.slug));
    try {
      await http.put(`/onb/roles/${role.slug}`, { enabled: nextEnabled });
      toast.success(nextEnabled ? `${role.name} turned on` : `${role.name} turned off`);
      // Optimistic: push the role in/out of active set immediately — SSE will reconcile.
      setTailored((prev) => nextEnabled ? (prev.includes(role.slug) ? prev : [...prev, role.slug]) : prev.filter((s) => s !== role.slug));
    } catch (e) {
      toast.error(errText(e, "Could not update the role"));
    }
    setToggling((s) => { const n = new Set(s); n.delete(role.slug); return n; });
  };

  // Auto-open every dept once data loads so Active mode shows something immediately.
  useEffect(() => {
    if (depts.length && Object.keys(openDept).length === 0) {
      const o = {};
      for (const d of depts) o[d.slug] = true;
      setOpenDept(o);
    }
  }, [depts, openDept]);

  const grouped = useMemo(() => {
    const activeSet = new Set(tailored);
    const pool = mode === "active" ? agents.filter((a) => activeSet.has(a.slug)) : agents;
    const filtered = pool.filter((a) => {
      if (filters.department !== "all" && a.department !== filters.department) return false;
      if (filters.stage !== "all" && a.stage !== filters.stage) return false;
      if (filters.status !== "all") {
        const st = (readiness[a.slug] || {}).state;
        if (filters.status === "live" && st !== "live") return false;
        if (filters.status === "blocked" && st !== "blocked") return false;
        if (filters.status === "ready" && !["live", "verified", "configured"].includes(st)) return false;
        if (filters.status === "needs" && st !== "selected") return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!a.name.toLowerCase().includes(q) && !(a.one_liner || "").toLowerCase().includes(q) && !a.slug.includes(q)) return false;
      }
      return true;
    });
    const byDept = {};
    for (const a of filtered) { (byDept[a.department] ||= []).push(a); }
    return byDept;
  }, [mode, agents, tailored, filters, search, readiness]);

  const totals = useMemo(() => {
    const t = { active: tailored.length, atlas: agents.length };
    const live = Object.values(readiness).filter((r) => r.state === "live").length;
    const blocked = Object.values(readiness).filter((r) => r.state === "blocked").length;
    return { ...t, live, blocked };
  }, [agents, tailored, readiness]);

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="ai-team-page">
      <header className="mh-rise flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <Eyebrow>Your AI team</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">The fleet, <em className="text-muted-foreground">by department.</em></h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Every role Manahouse tailored for you, grouped by department. Switch to Full Atlas to see every operator in the library.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="ai-mode-toggle">
          <button onClick={() => setMode("active")} data-testid="ai-mode-active" className={`h-9 px-4 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors ${mode === "active" ? "bg-[#D4FF3A] text-black border-[#D4FF3A]" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>Active · {totals.active}</button>
          <button onClick={() => setMode("atlas")} data-testid="ai-mode-atlas" className={`h-9 px-4 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors ${mode === "atlas" ? "bg-white text-black border-white" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>Full Atlas · {totals.atlas}</button>
        </div>
      </header>

      <Panel cls="p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-4 gap-5" testId="ai-totals-panel">
        <div><div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Active roles</div><div className="font-display text-3xl mt-1" style={{ color: "#D4FF3A" }}>{totals.active}</div></div>
        <div><div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Live now</div><div className="font-display text-3xl mt-1">{totals.live}</div></div>
        <div><div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Blocked</div><div className="font-display text-3xl mt-1" style={{ color: totals.blocked ? "#FF3B5C" : undefined }}>{totals.blocked}</div></div>
        <div><div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Atlas total</div><div className="font-display text-3xl mt-1 text-muted-foreground">{totals.atlas}</div></div>
      </Panel>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="ai-filters">
        <div className="relative lg:col-span-1 sm:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search roles…" data-testid="ai-search"
            className="w-full pl-9 h-10 rounded-full bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-white/30" />
        </div>
        <Filter value={filters.department} onChange={(v) => setFilters((f) => ({ ...f, department: v }))} testId="ai-f-dept"
          options={depts.map((d) => ({ value: d.slug, label: d.name }))} placeholder="All departments" />
        <Filter value={filters.stage} onChange={(v) => setFilters((f) => ({ ...f, stage: v }))} testId="ai-f-stage"
          options={["Foundation", "Capture", "Generate", "Orchestrate"].map((s) => ({ value: s, label: s }))} placeholder="All stages" />
        <Filter value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} testId="ai-f-status"
          options={[{ value: "live", label: "Live" }, { value: "ready", label: "Ready (config/verified)" }, { value: "needs", label: "Selected · needs setup" }, { value: "blocked", label: "Blocked" }]} placeholder="All statuses" />
        <button onClick={() => { setFilters({ department: "all", stage: "all", status: "all" }); setSearch(""); }} data-testid="ai-reset-filters"
          className="h-10 rounded-full border border-white/10 text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">Reset</button>
      </div>

      <div className="space-y-3">
        {depts.map((d) => {
          const list = grouped[d.slug] || [];
          if (filters.department !== "all" && d.slug !== filters.department) return null;
          if (mode === "active" && list.length === 0 && !search && filters.status === "all") return null;
          return (
            <DeptFolder key={d.slug} dept={d} roles={list} readinessMap={readiness} activeSet={new Set(tailored)}
              open={openDept[d.slug] ?? true} onToggle={() => setOpenDept((o) => ({ ...o, [d.slug]: !(o[d.slug] ?? true) }))}
              mode={mode === "active" ? "ACTIVE" : "ATLAS"}
              onRoleToggle={toggleRole} togglingSet={toggling} />
          );
        })}
        {Object.values(grouped).every((r) => r.length === 0) && (
          <p className="text-sm text-muted-foreground text-center py-10" data-testid="ai-empty">No roles match these filters.</p>
        )}
      </div>
    </div>
  );
}
